import React, { useState } from 'react';
import { columnKeyOf } from '../../utils/columnLayout';

const BTN = {
  padding: '0.15rem 0.4rem',
  backgroundColor: '#fff',
  color: '#334155',
  border: '1px solid #cbd5e1',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: '0.75rem',
  lineHeight: 1.2,
};

/**
 * Shared column visibility + reorder list (Master Component List style).
 * Drag the ⠿ handle or use ↑ / ↓. Optional 📌 freeze when the parent passes
 * isFrozen / onToggleFreeze. Layout is personalized by the parent (localStorage).
 */
const ColumnConfigPanel = ({
  columns = [],
  isVisible,
  onToggle,
  onMove,
  onShowAll,
  onHideAll,
  onReset,
  enableSearch = true,
  getLabel = (col) => col.label || col.header || columnKeyOf(col),
  lockedKeys,
  title,
  compact = true,
  isFrozen,
  onToggleFreeze,
  onClearFrozen,
  maxFrozen = 6,
}) => {
  const [query, setQuery] = useState('');
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const q = query.trim().toLowerCase();
  const filterActive = !!q;
  const locked = lockedKeys instanceof Set ? lockedKeys : new Set(lockedKeys || []);

  const visibleCount = columns.filter((c) => isVisible(columnKeyOf(c))).length;
  const frozenCount = onToggleFreeze
    ? columns.filter((c) => isFrozen && isFrozen(columnKeyOf(c))).length
    : 0;

  return (
    <div>
      {title && (
        <div style={{
          fontWeight: 600,
          marginBottom: 8,
          color: '#374151',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: 8,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
          fontSize: compact ? '0.8rem' : '0.9rem',
        }}>
          <span>{title} ({visibleCount}/{columns.length})</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {onShowAll && (
              <button type="button" onClick={onShowAll} style={{ ...BTN, fontSize: '0.65rem' }}>All</button>
            )}
            {onHideAll && (
              <button type="button" onClick={onHideAll} style={{ ...BTN, fontSize: '0.65rem' }}>Min</button>
            )}
            {onReset && (
              <button type="button" onClick={onReset} style={{ ...BTN, fontSize: '0.65rem' }}>Reset</button>
            )}
            {onClearFrozen && (
              <button type="button" onClick={onClearFrozen} style={{ ...BTN, fontSize: '0.65rem' }}>Unpin</button>
            )}
          </div>
        </div>
      )}
      {!title && (onShowAll || onHideAll || onReset) && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
          {onShowAll && <button type="button" onClick={onShowAll} style={BTN}>All</button>}
          {onHideAll && <button type="button" onClick={onHideAll} style={BTN}>Min</button>}
          {onReset && <button type="button" onClick={onReset} style={BTN}>Reset</button>}
          {onClearFrozen && <button type="button" onClick={onClearFrozen} style={BTN}>Unpin</button>}
        </div>
      )}
      {enableSearch && columns.length > 8 && (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="🔍 Filter columns…"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '5px 8px',
            marginBottom: 6,
            border: '1px solid #e5e7eb',
            borderRadius: 5,
            fontSize: '0.75rem',
            outline: 'none',
          }}
        />
      )}
      <p style={{ margin: '0 0 6px', fontSize: '0.7rem', color: '#94a3b8' }}>
        Drag ⠿ or use ↑ ↓ to rearrange.
        {onToggleFreeze ? ' Pin 📌 to freeze a column on the left while you scroll.' : ''}
        {' '}Your layout is saved on this browser.
        {filterActive ? ' Clear the filter to reorder.' : ''}
      </p>
      <div style={{ maxHeight: compact ? 320 : 420, overflowY: 'auto' }}>
        {columns.map((col, index) => {
          const key = columnKeyOf(col);
          const label = getLabel(col);
          if (q && !label.toLowerCase().includes(q) && !String(key).toLowerCase().includes(q)) {
            return null;
          }
          const checked = isVisible(key);
          const canHide = !locked.has(key);
          const isOver = overIndex === index && dragIndex !== null && dragIndex !== index;
          const frozen = !!(onToggleFreeze && isFrozen && isFrozen(key));
          const atMaxFreeze = onToggleFreeze && !frozen && frozenCount >= maxFrozen;
          return (
            <div
              key={key}
              draggable={!filterActive}
              onDragStart={(e) => {
                if (filterActive) return;
                setDragIndex(index);
                e.dataTransfer.effectAllowed = 'move';
                try { e.dataTransfer.setData('text/plain', key); } catch { /* IE */ }
              }}
              onDragOver={(e) => {
                if (filterActive || dragIndex === null) return;
                e.preventDefault();
                setOverIndex(index);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragIndex !== null && dragIndex !== index) onMove(dragIndex, index);
                setDragIndex(null);
                setOverIndex(null);
              }}
              onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: compact ? '3px 2px' : '6px 4px',
                fontSize: compact ? '0.78rem' : '0.85rem',
                color: '#374151',
                borderBottom: '1px solid #f1f5f9',
                backgroundColor: isOver ? '#e0f2fe' : (dragIndex === index ? '#f8fafc' : 'transparent'),
                opacity: checked ? 1 : 0.55,
              }}
            >
              <span
                title={filterActive ? 'Clear filter to reorder' : 'Drag to reorder'}
                style={{ cursor: filterActive ? 'not-allowed' : 'grab', color: '#94a3b8', fontSize: '0.75rem', userSelect: 'none' }}
              >
                ⠿
              </span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, cursor: canHide ? 'pointer' : 'default', minWidth: 0 }}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!canHide && checked}
                  onChange={() => { if (canHide || !checked) onToggle(key); }}
                  style={{ accentColor: '#6366f1' }}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
              </label>
              {onToggleFreeze && (
                  <button
                    type="button"
                    style={{
                      ...BTN,
                      backgroundColor: frozen ? '#eef2ff' : '#fff',
                      color: frozen ? '#4338ca' : '#94a3b8',
                      borderColor: frozen ? '#c7d2fe' : '#cbd5e1',
                      opacity: atMaxFreeze ? 0.45 : 1,
                      cursor: atMaxFreeze ? 'not-allowed' : 'pointer',
                    }}
                    disabled={atMaxFreeze}
                    onClick={() => onToggleFreeze(key)}
                    title={
                      frozen
                        ? 'Unfreeze this column'
                        : atMaxFreeze
                          ? `Maximum ${maxFrozen} frozen columns`
                          : 'Freeze this column on the left'
                    }
                  >
                    📌
                  </button>
              )}
              <button
                type="button"
                style={BTN}
                disabled={filterActive || index <= 0}
                onClick={() => onMove(index, index - 1)}
                title={filterActive ? 'Clear filter to reorder' : 'Move up'}
              >
                ↑
              </button>
              <button
                type="button"
                style={BTN}
                disabled={filterActive || index === columns.length - 1}
                onClick={() => onMove(index, index + 1)}
                title={filterActive ? 'Clear filter to reorder' : 'Move down'}
              >
                ↓
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ColumnConfigPanel;
